import { Injectable, signal } from '@angular/core';
import { useTranslate } from '../shared/utils/translation-utils';

/**
 * Options for {@link LoadingOverlayService.show}. All fields are optional;
 * resolution priority is `translationKey` > `loadingText` > template fallback.
 */
export interface LoadingOverlayOptions {
  /**
   * Fully-qualified Transloco key (e.g. `'dueDates.newDueDate.documentAnalyze.analyzingPrefill'`).
   * Resolved through `useTranslate` so cross-scope keys load correctly.
   */
  translationKey?: string;
  /** Interpolation params for the translation. Ignored when `translationKey` is not set. */
  translationParams?: Record<string, unknown>;
  /** Plain text shown verbatim. Used only when `translationKey` is not set. */
  loadingText?: string;
}

/**
 * App-wide controller for the full-screen loading overlay rendered once in `AppShellComponent`.
 *
 * Use it to block the UI during long-running operations (e.g. PDF analysis, bulk imports)
 * without each feature having to render its own backdrop.
 *
 * The overlay's text is selected by priority:
 * 1. `translationKey` (+ optional `translationParams`) — translated via `useTranslate`.
 * 2. `loadingText` — displayed verbatim, no translation.
 * 3. Otherwise the app-shell template renders the default `common.loading` translation.
 *
 * @example
 * private readonly _loadingOverlay = inject(LoadingOverlayService);
 *
 * async analyze(file: File) {
 *   this._loadingOverlay.show({ translationKey: 'dueDates.newDueDate.documentAnalyze.analyzingPrefill' });
 *   try {
 *     await this._service.analyze(file);
 *   } finally {
 *     this._loadingOverlay.hide();
 *   }
 * }
 */
@Injectable({ providedIn: 'root' })
export class LoadingOverlayService {
  /** No prefix — callers always pass fully-qualified keys. */
  private readonly _t = useTranslate();

  private readonly _visible = signal(false);
  /** Resolved text to show. `null` signals "no caller-supplied text" so the template renders the fallback. */
  private readonly _text = signal<string | null>(null);

  /** Whether the overlay should currently be rendered. Bound by `AppShellComponent`. */
  public readonly visible = this._visible.asReadonly();
  /** Caller-supplied text once resolved, or `null` when the template should use its fallback translation. */
  public readonly text = this._text.asReadonly();

  /**
   * Show the overlay. Calling `show` while the overlay is already visible replaces the text.
   *
   * For the `translationKey` branch, visibility is flipped only after the translation resolves
   * to avoid a flash of the fallback text while the scope is loading.
   *
   * @param options Optional text source. Omit entirely to display the default `common.loading` translation.
   */
  public show(options?: LoadingOverlayOptions): void {
    if (options?.translationKey) {
      this._t({
        key: options.translationKey,
        params: options.translationParams
      }).then((value) => {
        this._text.set(value as string);
        this._visible.set(true);
      });
    } else if (options?.loadingText) {
      this._text.set(options.loadingText);
      this._visible.set(true);
    } else {
      this._text.set(null);
      this._visible.set(true);
    }
  }

  /** Hide the overlay. Safe to call when it's already hidden. */
  public hide(): void {
    this._visible.set(false);
  }
}
